/*
 * app.review-schedule.js - Today's review across sessions, and spacing that knows the exam
 *
 * Spaced Review (app.core.js) schedules cards inside one session. This module looks across
 * all of them: it reads every saved session's review schedule (srsDataByQuestionKey) and
 * the calendar's exams, and publishes window.MemoraReviewSchedule:
 *
 *   refresh({ force })   re-reads sessions and exams; resolves to the Today summary
 *   summary()            the last summary, or null before the first read
 *   start(sessionId?)    opens the most urgent session (or the given one) and starts
 *                        Spaced Review over cards the learner has already seen
 *   clampNextReview(nextReview, now, sessionId)
 *                        shortens an interval that would carry a card past the next exam
 *   isExamCrunchDue(record, now)
 *                        in the last two days before an exam, shaky and once-missed cards
 *                        come back once
 *   dashboardMarkup(), deskMarkup(), dashboardAction(action, button)
 *                        the Today row, or the review half and exam line of the Dashboard's
 *                        Study Desk (Polish renders them in place)
 *   forecastCheck()      how well the readiness forecast has predicted due-card answers
 *   showDoneForToday()   the Done for today card, as it shows after the last set
 *   nudgesOn(), setNudges(on)
 *                        whether set breaks, the Done card, the reminder and combo pop-ups
 *                        show; "Don't show this again" turns them off, Settings back on
 *
 * Which sessions an exam covers: by default, every session studied since the exam before
 * it. The learner can change that from the Today row; the choice is stored in IndexedDB
 * settings under memora_exam_coverage_v1. The session records are only read; Spaced Review
 * writes them as it always has.
 *
 * Today is finite. Each session's due cards are one set; between sets a corner note names
 * the next, and after the last one a Done for today card shows the readiness forecast,
 * tomorrow's load, a daily study time for the calendar and the once-a-day reminder.
 * The forecast's check log is in IndexedDB settings (memora_readiness_log_v1); the day's
 * starting forecast, review hours, study slot and reminder are small localStorage keys.
 */
(function (global) {
    'use strict';

    const d = global.document;
    if (!d || global.MemoraReviewSchedule) return;

    const DAY_MS = 24 * 60 * 60 * 1000;
    const CALENDAR_STORE_KEY = 'memora_calendar_events_v1';
    const COVERAGE_STORE_KEY = 'memora_exam_coverage_v1';
    const UPDATED_EVENT = 'memora:today-review-updated';
    const SECONDS_PER_CARD = 40;
    const CRUNCH_WINDOW_MS = 2 * DAY_MS;
    const MIN_CLAMPED_INTERVAL_MS = 12 * 60 * 60 * 1000;
    // An all-day exam has no start time; 8 am is the earliest it is likely to begin.
    const ALL_DAY_EXAM_START_MINUTES = 8 * 60;
    const REFRESH_THROTTLE_MS = 3000;
    const MAX_SOURCE_CHIPS = 4;
    const WEAK_CONFIDENCE = new Set(['again', 'shaky']);
    const COVER_OPEN_CLASS = 'is-coverage-open';
    const COVER_MOTION_MS = 580; // the hero row's transition in live-left-rail-polish.css
    const COVER_MIN_GROWTH = 120; // px the Coming up card always grows by when Change opens
    // Readiness forecast. Recall decays as (1 + F·t/S)^-0.5, so a card reviewed on its interval
    // S is recalled 90% of the time; the learner's own hit rate on due cards rescales S.
    const FORGETTING_F = 19 / 81;
    const FORECAST_NO_EXAM_DAYS = 7;
    const FORECAST_MIN_CARDS = 5;
    const CALIBRATION_STORE_KEY = 'memora_readiness_log_v1';
    const CALIBRATION_MAX = 400;
    const CALIBRATION_PRIOR_CARDS = 20;
    const CALIBRATION_PRIOR_RATE = 0.9;
    const BASELINE_STORAGE_KEY = 'memora_readiness_baseline_v1';
    // Finishing Today: the pause between sets, the Done card, study slots and the reminder.
    const SET_BREAK_SECONDS = 3;
    const DONE_CARD_MS = 40000;
    const HOURS_STORAGE_KEY = 'memora_review_hours_v1';
    const SLOT_STORAGE_KEY = 'memora_study_slot_v1';
    const REMINDER_STORAGE_KEY = 'memora_daily_reminder_v1';
    // "Don't show this again" on the set break or Done card: no corner notes, reminder or
    // combo pop-ups until Settings → Appearance turns them back on.
    const NUDGES_STORAGE_KEY = 'memora_study_nudges_v1';
    const DEFAULT_STUDY_MINUTES = 19 * 60;
    const EARLIEST_SLOT_MINUTES = 6 * 60;
    const LATEST_SLOT_MINUTES = 21 * 60;
    const SLOT_MINUTES = 30;
    const SLOT_WEEKS = 8;
    const REMINDER_CHECK_MS = 60 * 1000;
    const QUIET_FROM_MINUTES = 22 * 60;
    const QUIET_UNTIL_MINUTES = 7 * 60;
    const REMINDER_MAX_UNOPENED = 3;
    const SLOT_NOTE = "Today's review: the cards due across your sessions.";

    const state = {
        summary: null,
        exams: [],
        coverage: {},
        sessionActivity: new Map(),
        sessionNames: new Map(),
        reading: null,
        readAt: 0,
        coverageOpen: false,
        coverageRow: 0,
        coverageFocusTimer: 0,
        coverageDraft: null,
        breakdownOpen: false,
        starting: false,
        calibration: null,
        calibrationLoading: null,
        calibrationSaveTimer: 0,
        setBreakTimer: 0,
        doneCard: null,
        doneTimer: 0
    };

    /* --- Small helpers ------------------------------------------------------ */

    const esc = (value) => (typeof escapeHtml === 'function'
        ? escapeHtml(String(value ?? ''))
        : String(value ?? '').replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`));

    function currentId() {
        return typeof currentSessionId === 'undefined' ? '' : String(currentSessionId || '');
    }

    function dayStart(dayKey) {
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey || ''));
        if (!match) return NaN;
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
    }

    function hasHistory(record) {
        if (!record || typeof record !== 'object') return false;
        return (Number(record.lastReviewed) || 0) > 0
            || (Number(record.repetitions) || 0) > 0
            || (Number(record.lapses) || 0) > 0;
    }

    function isWeak(record) {
        const quality = Number(record?.lastQuality);
        return (Number(record?.lapses) || 0) > 0
            || WEAK_CONFIDENCE.has(String(record?.lastConfidence || '').toLowerCase())
            || (Number.isFinite(quality) && quality < 4);
    }

    // Question keys start with the normalized source file ("renal_lecture_3.pdf::stem...").
    function sourceLabel(key) {
        const raw = String(key || '').split('::')[0] || '';
        if (!raw || raw === 'nosource') return 'Other';
        return raw
            .replace(/_quiz$/i, '')
            .replace(/\.(?:json|pdf|pptx?|txt)$/i, '')
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (ch) => ch.toUpperCase()) || 'Other';
    }

    function sessionDisplayName(session) {
        try {
            const entry = typeof buildSessionIndexEntry === 'function' ? buildSessionIndexEntry(session) : session;
            if (typeof formatWelcomeSessionDisplayName === 'function') return String(formatWelcomeSessionDisplayName(entry) || 'Study session');
            return String(entry?.customName || entry?.name || 'Study session');
        } catch (_error) {
            return 'Study session';
        }
    }

    function formatDay(time) {
        try {
            return new Date(time).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        } catch (_error) {
            return '';
        }
    }

    // Calendar days between the two dates, so a 2 pm exam today reads "today".
    function daysUntil(time, now = Date.now()) {
        const dayOf = (value) => {
            const date = new Date(value);
            return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        };
        return Math.round((dayOf(time) - dayOf(now)) / DAY_MS);
    }

    function inDaysCopy(days) {
        if (days <= 0) return 'today';
        if (days === 1) return 'tomorrow';
        return `in ${days} days`;
    }

    function localDayKey(time = Date.now()) {
        const date = new Date(time);
        const pad = (value) => String(value).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    }

    function minutesOfDay(time = Date.now()) {
        const date = new Date(time);
        return date.getHours() * 60 + date.getMinutes();
    }

    function formatMinutes(minutes) {
        try {
            return new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60)
                .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
        } catch (_error) {
            return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
        }
    }

    function plural(count, word) {
        return `${count} ${word}${count === 1 ? '' : 's'}`;
    }

    // Small per-device preferences; a blocked or private store just forgets them.
    function readLocal(key, fallback) {
        try {
            const value = JSON.parse(global.localStorage?.getItem(key) || 'null');
            return value ?? fallback;
        } catch (_error) {
            return fallback;
        }
    }

    function writeLocal(key, value) {
        try {
            global.localStorage?.setItem(key, JSON.stringify(value));
        } catch (_error) { /* the preference lasts this page only */ }
    }

    /* --- Exams and coverage ------------------------------------------------- */

    // An exam counts until it starts: a timed exam at its start time, an all-day one at
    // ALL_DAY_EXAM_START_MINUTES on its day. Coverage, spacing, the final pass and the
    // Dashboard copy all use this one deadline.
    function examStart(occurrence) {
        const start = dayStart(occurrence?.startDay);
        if (!Number.isFinite(start)) return NaN;
        const minutes = !occurrence.allDay && Number.isInteger(occurrence.startMinutes)
            ? occurrence.startMinutes
            : ALL_DAY_EXAM_START_MINUTES;
        return start + minutes * 60 * 1000;
    }

    // The calendar's own expansion (app.calendar.js) localizes zoned events and expands
    // repeats; it is loaded with the Dashboard's Coming up card. Until it is, a stored
    // event is read as it was saved, localized the same way when it carries a zone.
    function examOccurrences(event, now) {
        if (typeof expandCalendarRecurrence === 'function' && typeof calendarDayKeyFromTimestamp === 'function'
            && typeof calendarAddDays === 'function') {
            try {
                const today = calendarDayKeyFromTimestamp(now);
                return expandCalendarRecurrence(event, calendarAddDays(today, -400), calendarAddDays(today, 730));
            } catch (error) {
                console.warn('[Today review] An exam could not be expanded.', event?.id, error);
            }
        }
        if (event.timezone && event.sourceStart && typeof calendarLocalizeZonedEvent === 'function') {
            try {
                return [calendarLocalizeZonedEvent({
                    ...event,
                    startDay: event.sourceStart.dayKey,
                    endDay: event.sourceEnd?.dayKey || event.sourceStart.dayKey,
                    _sourceZone: true
                })];
            } catch (_error) { /* read as saved below */ }
        }
        return [event];
    }

    // Exams are calendar events of type exam, plus practicals filed under another type.
    function readExams(stored, now = Date.now()) {
        const events = stored?.events && typeof stored.events === 'object' ? Object.values(stored.events) : [];
        const exams = [];
        events
            .filter((event) => event && (event.type === 'exam' || /\bpractic(?:al|um)\b/i.test(String(event.title || ''))))
            .forEach((event) => {
                const occurrences = examOccurrences(event, now);
                occurrences.forEach((occurrence) => {
                    const time = examStart(occurrence);
                    if (!Number.isFinite(time)) return;
                    const baseId = String(event.id || `${event.startDay}:${event.title}`);
                    exams.push({
                        id: occurrences.length > 1 ? `${baseId}@${occurrence.startDay}` : baseId,
                        title: String(event.title || 'Exam').trim() || 'Exam',
                        time
                    });
                });
            });
        return exams.sort((a, b) => a.time - b.time);
    }

    function previousExamTime(exam) {
        let previous = -Infinity;
        state.exams.forEach((other) => {
            if (other.time < exam.time && other.time > previous) previous = other.time;
        });
        return previous;
    }

    function isExplicitCoverage(exam) {
        return Array.isArray(state.coverage?.[exam.id]);
    }

    function examCoversSession(exam, sessionId) {
        if (!exam || !sessionId) return false;
        if (isExplicitCoverage(exam)) return state.coverage[exam.id].includes(sessionId);
        const lastActivity = state.sessionActivity.get(sessionId);
        // A session studied since the exam before this one is this exam's material.
        return Number.isFinite(lastActivity) && lastActivity >= previousExamTime(exam) && lastActivity < exam.time;
    }

    function examForSession(sessionId, now = Date.now()) {
        if (!sessionId) return null;
        return state.exams.find((exam) => exam.time > now && examCoversSession(exam, sessionId)) || null;
    }

    function coveredSessionIds(exam) {
        return Array.from(state.sessionActivity.keys()).filter((id) => examCoversSession(exam, id));
    }

    async function saveCoverage(examId, sessionIds) {
        const next = { ...(state.coverage || {}), [examId]: Array.from(new Set(sessionIds)) };
        state.coverage = next;
        try {
            await IDB.put('settings', COVERAGE_STORE_KEY, next);
        } catch (error) {
            console.warn('[Today review] Exam coverage could not be saved.', error);
            if (typeof showPopup === 'function') showPopup('Could not save which sessions this exam covers. Try again.', null, true);
        }
    }

    /* --- Exam-aware spacing (called by app.core.js) --------------------------- */

    // A card whose next review would land in the last day before its exam, or after it,
    // comes back halfway through the time left instead, so it is seen before the exam.
    // Repeated reviews close in on the exam; an interval that already lands earlier is kept.
    // A clamped review is always at least MIN_CLAMPED_INTERVAL_MS out and so always before
    // the exam starts. Within that last stretch a card just rated is not brought back again:
    // it has had its final review, and the weak ones already came back in the final pass.
    function clampNextReview(nextReview, now = Date.now(), sessionId = currentId()) {
        if (!Number.isFinite(nextReview)) return nextReview;
        const exam = examForSession(String(sessionId || ''), now);
        if (!exam) return nextReview;
        const remaining = exam.time - now;
        if (remaining <= MIN_CLAMPED_INTERVAL_MS) return nextReview;
        if (nextReview < exam.time - DAY_MS) return nextReview;
        const latest = now + Math.max(MIN_CLAMPED_INTERVAL_MS, (remaining - DAY_MS) / 2);
        return Math.min(nextReview, latest);
    }

    function isCrunchDueFor(record, exam, now) {
        if (!exam || !hasHistory(record) || !isWeak(record)) return false;
        const remaining = exam.time - now;
        if (remaining <= 0 || remaining > CRUNCH_WINDOW_MS) return false;
        return (Number(record.lastReviewed) || 0) < exam.time - CRUNCH_WINDOW_MS;
    }

    function isExamCrunchDue(record, now = Date.now()) {
        return isCrunchDueFor(record, examForSession(currentId(), now), now);
    }

    /* --- Readiness forecast --------------------------------------------------- */

    // The forecast is the expected share of cards the learner would recall at a horizon: the
    // next exam for the sessions it covers, or a week out when no exam covers any. It is an
    // estimate from the schedule, so the copy says "expected", and forecastCheck() reports how
    // well it has predicted real answers on due cards.
    function recallAt(elapsedDays, stability) {
        if (!(elapsedDays > 0)) return 1;
        return Math.pow(1 + (FORGETTING_F * elapsedDays) / Math.max(0.1, stability), -0.5);
    }

    // The interval Spaced Review gave the card, in days; older records only carry the gap.
    function recordInterval(record) {
        const interval = Number(record?.interval);
        if (interval > 0) return interval;
        const gap = (Number(record?.nextReview) - Number(record?.lastReviewed)) / DAY_MS;
        return Number.isFinite(gap) && gap > 0 ? gap : 1;
    }

    function elapsedDays(record, now) {
        const last = Number(record?.lastReviewed) || 0;
        return last > 0 ? Math.max(0, (now - last) / DAY_MS) : recordInterval(record);
    }

    // A learner who gets 90% of due cards right is recalled as the curve expects (scale 1);
    // a lower hit rate shrinks every stability, a higher one grows it. Twenty imagined
    // reviews at 90% keep the first few real ones from swinging it.
    function stabilityScale(log = state.calibration) {
        const entries = Array.isArray(log) ? log : [];
        const hits = entries.reduce((sum, entry) => sum + (entry.y ? 1 : 0), 0);
        const rate = (hits + CALIBRATION_PRIOR_RATE * CALIBRATION_PRIOR_CARDS) / (entries.length + CALIBRATION_PRIOR_CARDS);
        const bounded = Math.min(0.99, Math.max(0.5, rate));
        return Math.min(4, Math.max(0.25, FORGETTING_F / (Math.pow(bounded, -2) - 1)));
    }

    // Expected recall at the horizon as things stand, and if the card were reviewed now: a hit
    // grows its interval by its ease, a miss sends it back to a day.
    function cardForecast(record, now, horizon, scale, isDue) {
        const interval = recordInterval(record);
        const elapsed = elapsedDays(record, now);
        const ahead = Math.max(0, (horizon - now) / DAY_MS);
        const atHorizon = recallAt(elapsed + ahead, scale * interval);
        if (!isDue) return { atHorizon, cleared: atHorizon };
        const recalledNow = recallAt(elapsed, scale * interval);
        const grown = Math.max(1, interval * (Number(record?.ef) || 2.5));
        const cleared = recalledNow * recallAt(ahead, scale * grown) + (1 - recalledNow) * recallAt(ahead, scale);
        return { atHorizon, cleared: Math.max(atHorizon, cleared) };
    }

    // The first forecast of each day is the one later deltas count from.
    function forecastDelta(label, value, now) {
        const today = localDayKey(now);
        const stored = readLocal(BASELINE_STORAGE_KEY, null);
        if (!stored || stored.day !== today || stored.label !== label || !Number.isFinite(stored.value)) {
            writeLocal(BASELINE_STORAGE_KEY, { day: today, label, value });
            return 0;
        }
        return value - stored.value;
    }

    function combineForecast(entries, context, now) {
        let cards = 0;
        let sum = 0;
        let cleared = 0;
        const sources = new Map();
        entries.forEach((entry) => {
            const part = entry.forecast;
            if (!part) return;
            cards += part.cards;
            sum += part.sum;
            cleared += part.cleared;
            part.sources.forEach((value, label) => {
                const total = sources.get(label) || { cards: 0, sum: 0 };
                total.cards += value.cards;
                total.sum += value.sum;
                sources.set(label, total);
            });
        });
        if (cards < FORECAST_MIN_CARDS) return null;
        const label = context.exam ? 'Exam day' : 'In a week';
        const value = sum / cards;
        const weakest = sources.size > 1
            ? Array.from(sources, ([source, total]) => ({ label: source, value: total.sum / total.cards, cards: total.cards }))
                .filter((source) => source.cards >= 3)
                .sort((a, b) => a.value - b.value)
                .slice(0, 2)
            : [];
        return {
            label,
            examTitle: context.exam?.title || '',
            value,
            ifCleared: cleared / cards,
            cards,
            weakest,
            delta: forecastDelta(label, value, now)
        };
    }

    const percent = (value) => `${Math.round(value * 100)}%`;

    function forecastCopy(forecast, withCleared = false) {
        if (!forecast) return '';
        const base = `${forecast.label} ${percent(forecast.value)}`;
        return withCleared && Math.round(forecast.ifCleared * 100) > Math.round(forecast.value * 100)
            ? `${base} → ${percent(forecast.ifCleared)}`
            : base;
    }

    function forecastTitle(forecast) {
        if (!forecast) return '';
        const when = forecast.examTitle ? `on ${forecast.examTitle}` : 'a week from now';
        const cleared = Math.round(forecast.ifCleared * 100) > Math.round(forecast.value * 100)
            ? ` Clearing today's cards raises it to ${percent(forecast.ifCleared)}.`
            : '';
        return `Expected share of ${plural(forecast.cards, 'reviewed card')} you would recall ${when}, estimated from your review history.${cleared}`;
    }

    function loadCalibration() {
        if (Array.isArray(state.calibration)) return Promise.resolve(state.calibration);
        if (!state.calibrationLoading) {
            state.calibrationLoading = (typeof IDB === 'undefined'
                ? Promise.resolve(null)
                : IDB.get('settings', CALIBRATION_STORE_KEY).catch(() => null))
                .then((stored) => {
                    const valid = (Array.isArray(stored) ? stored : [])
                        .filter((entry) => entry && Number.isFinite(entry.p) && (entry.y === 0 || entry.y === 1));
                    // Ratings made while the log loaded are kept after the stored ones.
                    state.calibration = valid.concat(Array.isArray(state.calibration) ? state.calibration : []).slice(-CALIBRATION_MAX);
                    return state.calibration;
                })
                .finally(() => { state.calibrationLoading = null; });
        }
        return state.calibrationLoading;
    }

    // A rating on a due card checks the forecast: what it predicted for this card against
    // whether it was answered right. updateStats fires before Spaced Review updates the
    // record, so the record read here is the one the prediction was made from.
    function logCalibration(event) {
        const idx = Number(event?.detail?.questionIndex);
        if (!Number.isInteger(idx) || typeof srsData === 'undefined' || typeof statsData === 'undefined') return;
        const record = srsData?.[idx];
        const now = Date.now();
        if (!hasHistory(record)) return;
        const nextReview = Number(record.nextReview);
        if (Number.isFinite(nextReview) && nextReview > now) return;
        const attempts = statsData?.[idx];
        const last = Array.isArray(attempts) ? attempts[attempts.length - 1] : null;
        if (!last || typeof last.correct !== 'boolean') return;
        const p = recallAt(elapsedDays(record, now), stabilityScale() * recordInterval(record));
        const entry = { p: Math.round(p * 1000) / 1000, y: last.correct ? 1 : 0, at: now };
        void loadCalibration().then((log) => {
            log.push(entry);
            if (log.length > CALIBRATION_MAX) log.splice(0, log.length - CALIBRATION_MAX);
            global.clearTimeout(state.calibrationSaveTimer);
            state.calibrationSaveTimer = global.setTimeout(() => {
                IDB.put('settings', CALIBRATION_STORE_KEY, log.slice())
                    .catch((error) => console.warn('[Today review] The forecast check log could not be saved.', error));
            }, 1500);
        });
    }

    // Brier score against always guessing the learner's own hit rate: skill above 0 means the
    // forecast tells due cards apart better than the average does.
    function forecastCheck() {
        const log = Array.isArray(state.calibration) ? state.calibration : [];
        if (!log.length) return { reviews: 0, scale: stabilityScale(log) };
        const mean = (values) => values.reduce((sum, value) => sum + value, 0) / values.length;
        const hitRate = mean(log.map((entry) => entry.y));
        const brier = mean(log.map((entry) => (entry.p - entry.y) ** 2));
        const baselineBrier = mean(log.map((entry) => (hitRate - entry.y) ** 2));
        const edges = [0, 0.5, 0.7, 0.8, 0.9, 1.01];
        const bins = edges.slice(0, -1).map((low, index) => {
            const inBin = log.filter((entry) => entry.p >= low && entry.p < edges[index + 1]);
            return {
                range: `${Math.round(low * 100)}-${Math.min(100, Math.round(edges[index + 1] * 100))}%`,
                reviews: inBin.length,
                predicted: inBin.length ? mean(inBin.map((entry) => entry.p)) : null,
                actual: inBin.length ? mean(inBin.map((entry) => entry.y)) : null
            };
        });
        return {
            reviews: log.length,
            hitRate,
            scale: stabilityScale(log),
            brier,
            baselineBrier,
            skill: baselineBrier > 0 ? 1 - brier / baselineBrier : null,
            bins
        };
    }

    /* --- Reading sessions ----------------------------------------------------- */

    // The open session is counted from memory, since its latest ratings may not be saved yet.
    function liveSessionRecords() {
        if (typeof questions === 'undefined' || !Array.isArray(questions)) return null;
        if (typeof srsData === 'undefined' || !srsData || typeof buildQuestionPersistenceKeyFromQuestion !== 'function') return null;
        const records = [];
        questions.forEach((question, idx) => {
            const record = srsData[idx];
            if (!record) return;
            records.push({ key: buildQuestionPersistenceKeyFromQuestion(question), record });
        });
        return records;
    }

    // Today counts what Start can open. Start loads a session with its selected sources, so
    // a closed session counts only cards in its selected banks - the same pool it has when
    // open. Saved progress for a deselected bank, or for a card since rewritten, stays out.
    async function selectedQuestionKeys(session, bankCache, bankLists = null) {
        const keys = new Set();
        const setNames = typeof getSessionActiveSetNames === 'function' ? getSessionActiveSetNames(session) : [];
        for (const setName of setNames) {
            if (/\.pdf$/i.test(setName)) continue;
            if (!bankCache.has(setName)) {
                bankCache.set(setName, IDB.get('questions', setName)
                    .then((stored) => (typeof normalizeStoredQuestionCollection === 'function'
                        ? normalizeStoredQuestionCollection(stored, setName)
                        : stored))
                    .catch(() => null));
            }
            const bank = await bankCache.get(setName);
            if (bankLists && Array.isArray(bank)) bankLists.push(bank);
            (Array.isArray(bank) ? bank : []).forEach((question) => {
                const key = buildQuestionPersistenceKeyFromQuestion(question);
                if (key) keys.add(key);
            });
        }
        return keys;
    }

    async function storedSessionRecords(session, bankCache) {
        const buckets = session?.data?.srsDataByQuestionKey;
        if (!buckets || typeof buckets !== 'object' || typeof buildQuestionPersistenceKeyFromQuestion !== 'function') return [];
        const bankLists = [];
        const selected = await selectedQuestionKeys(session, bankCache, bankLists);
        // A session saved before a key-format change keeps legacy keys until it is reopened.
        // Resolve them against its own banks, as opening it would; ambiguous keys stay out.
        const aliases = typeof getLegacyQuestionKeyAliasesForQuestions === 'function'
            ? getLegacyQuestionKeyAliasesForQuestions(Object.keys(buckets).filter((key) => !selected.has(key)), bankLists)
            : new Map();
        const records = [];
        Object.entries(buckets).forEach(([storedKey, values]) => {
            const key = selected.has(storedKey) ? storedKey : aliases.get(storedKey);
            if (!key || !selected.has(key)) return;
            // Opening the session keeps a current key's own record over a legacy one.
            if (key !== storedKey && Object.prototype.hasOwnProperty.call(buckets, key)) return;
            const list = Array.isArray(values) ? values : [values];
            const record = list.find((value) => value && typeof value === 'object');
            if (record) records.push({ key, record });
        });
        return records;
    }

    // context: the forecast's horizon and scale, the exam it is for, and when tomorrow ends.
    function summarizeSession(id, name, records, now, context = null) {
        const exam = examForSession(id, now);
        const sources = new Map();
        const forecasting = context && (!context.exam || examCoversSession(context.exam, id));
        const forecast = forecasting ? { cards: 0, sum: 0, cleared: 0, sources: new Map() } : null;
        let due = 0;
        let seen = 0;
        let tomorrow = 0;
        let nextDueAt = Infinity;
        records.forEach(({ key, record }) => {
            if (!hasHistory(record)) return;
            seen += 1;
            const nextReview = Number(record.nextReview);
            const isDue = !Number.isFinite(nextReview) || nextReview <= now || isCrunchDueFor(record, exam, now);
            const label = sourceLabel(key);
            if (isDue) {
                due += 1;
                sources.set(label, (sources.get(label) || 0) + 1);
            } else {
                if (nextReview < nextDueAt) nextDueAt = nextReview;
                if (context && nextReview < context.tomorrowEnd) tomorrow += 1;
            }
            if (forecast) {
                const card = cardForecast(record, now, context.horizon, context.scale, isDue);
                forecast.cards += 1;
                forecast.sum += card.atHorizon;
                forecast.cleared += card.cleared;
                const bySource = forecast.sources.get(label) || { cards: 0, sum: 0 };
                bySource.cards += 1;
                bySource.sum += card.atHorizon;
                forecast.sources.set(label, bySource);
            }
        });
        return { id, name, due, seen, tomorrow, nextDueAt, exam, sources, forecast };
    }

    async function read(now = Date.now()) {
        const [sessions, calendar, coverage] = await Promise.all([
            typeof readSessionStoreAllWithTimeout === 'function' ? readSessionStoreAllWithTimeout('sessions', 4000) : [],
            IDB.get('settings', CALENDAR_STORE_KEY).catch(() => null),
            IDB.get('settings', COVERAGE_STORE_KEY).catch(() => null)
        ]);
        state.exams = readExams(calendar, now);
        state.coverage = coverage && typeof coverage === 'object' ? coverage : {};
        state.sessionActivity = new Map();
        state.sessionNames = new Map();
        const list = (Array.isArray(sessions) ? sessions : []).filter((session) => session?.id && session.data);
        list.forEach((session) => {
            const at = Date.parse(session.timestamp || '') || Number(session.data?.resumeStateUpdatedAt) || 0;
            state.sessionActivity.set(String(session.id), at);
            state.sessionNames.set(String(session.id), sessionDisplayName(session));
        });
        const openId = currentId();
        if (openId && !state.sessionActivity.has(openId)) state.sessionActivity.set(openId, now);

        await loadCalibration();
        const upcomingExam = state.exams.find((exam) => exam.time > now) || null;
        // The forecast is for the next exam when it covers any session, else a week out.
        const forecastExam = upcomingExam && coveredSessionIds(upcomingExam).length ? upcomingExam : null;
        const tomorrowEnd = new Date(now);
        tomorrowEnd.setHours(0, 0, 0, 0);
        tomorrowEnd.setDate(tomorrowEnd.getDate() + 2);
        const context = {
            exam: forecastExam,
            horizon: forecastExam ? forecastExam.time : now + FORECAST_NO_EXAM_DAYS * DAY_MS,
            scale: stabilityScale(),
            tomorrowEnd: tomorrowEnd.getTime()
        };

        const live = liveSessionRecords();
        const bankCache = new Map();
        const summaries = await Promise.all(list.map(async (session) => {
            const id = String(session.id);
            const records = id === openId && live ? live : await storedSessionRecords(session, bankCache);
            return summarizeSession(id, sessionDisplayName(session), records, now, context);
        }));
        if (openId && live && !summaries.some((entry) => entry.id === openId)) {
            summaries.push(summarizeSession(openId, 'Current session', live, now, context));
        }

        const withHistory = summaries.filter((entry) => entry.seen > 0);
        const dueSessions = withHistory
            .filter((entry) => entry.due > 0)
            .sort((a, b) => {
                const examA = a.exam ? a.exam.time : Infinity;
                const examB = b.exam ? b.exam.time : Infinity;
                return (examA - examB) || (b.due - a.due);
            });
        const sources = new Map();
        dueSessions.forEach((entry) => entry.sources.forEach((count, label) => sources.set(label, (sources.get(label) || 0) + count)));
        const total = dueSessions.reduce((sum, entry) => sum + entry.due, 0);
        const tomorrow = withHistory.reduce((sum, entry) => sum + entry.tomorrow, 0);
        const nextDueAt = withHistory.reduce((min, entry) => Math.min(min, entry.nextDueAt), Infinity);

        return {
            computedAt: now,
            hasHistory: withHistory.length > 0,
            total,
            minutes: Math.max(1, Math.round((total * SECONDS_PER_CARD) / 60)),
            // Cards that come due after now and before tomorrow ends: tomorrow's load.
            tomorrow,
            tomorrowMinutes: tomorrow ? Math.max(1, Math.round((tomorrow * SECONDS_PER_CARD) / 60)) : 0,
            forecast: combineForecast(withHistory, context, now),
            sessions: dueSessions.map(({ id, name, due, exam }) => ({ id, name, due, examId: exam?.id || '' })),
            sources: Array.from(sources, ([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count),
            nextDueAt: Number.isFinite(nextDueAt) ? nextDueAt : 0,
            exam: upcomingExam
                ? { ...upcomingExam, days: daysUntil(upcomingExam.time, now), covered: coveredSessionIds(upcomingExam).length }
                : null
        };
    }

    function refresh({ force = false } = {}) {
        if (state.reading) return state.reading;
        if (!force && state.summary && Date.now() - state.readAt < REFRESH_THROTTLE_MS) return Promise.resolve(state.summary);
        if (typeof IDB === 'undefined') return Promise.resolve(state.summary);
        state.reading = read()
            .then((summary) => {
                state.summary = summary;
                state.readAt = Date.now();
                global.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail: summary }));
                return summary;
            })
            .catch((error) => {
                console.warn('[Today review] Could not read review schedules.', error);
                return state.summary;
            })
            .finally(() => { state.reading = null; });
        return state.reading;
    }

    /* --- Starting a review ---------------------------------------------------- */

    function waitFor(check, timeoutMs) {
        return new Promise((resolve) => {
            const startedAt = Date.now();
            const tick = () => {
                if (check()) return resolve(true);
                if (Date.now() - startedAt > timeoutMs) return resolve(false);
                global.setTimeout(tick, 60);
            };
            tick();
        });
    }

    async function start(sessionId = '') {
        if (state.starting) return false;
        state.starting = true;
        try {
            const summary = await refresh({ force: true });
            const target = sessionId
                ? summary?.sessions.find((entry) => entry.id === sessionId)
                : summary?.sessions[0];
            if (!target) {
                if (typeof showPopup === 'function') showPopup('Nothing is due for review right now. You are caught up.', null, false);
                return false;
            }
            rememberStudyTime();
            closeDoneCard();
            if (target.id !== currentId()) {
                if (typeof SessionManager === 'undefined') return false;
                const loaded = await SessionManager.load(target.id);
                const settled = await waitFor(() => currentId() === target.id
                    && !global.MemoraLiveLeftRailRouting?.isNavigating?.(), 12000);
                if (loaded === false || !settled) {
                    if (typeof showPopup === 'function') showPopup('Could not open that session for review. Try again.', null, true);
                    return false;
                }
            }
            global.MemoraLiveLeftRailPolish?.closeDashboard?.();
            global.landQuizInSoloLayout?.();
            await global.MemoraLiveLeftRailRouting?.returnToQuizzes?.();
            if (typeof activateAdaptiveReviewMode !== 'function') return false;
            return activateAdaptiveReviewMode(true, { seenOnly: true }) !== false;
        } finally {
            state.starting = false;
        }
    }

    /* --- Finishing Today ------------------------------------------------------ */

    function dismiss(node) {
        if (!node) return;
        node.classList.remove('show');
        global.setTimeout(() => node.remove(), 220);
    }

    function toastButton(label) {
        const button = d.createElement('button');
        button.type = 'button';
        button.className = 'quiz-action-toast-action';
        button.textContent = label;
        return button;
    }

    // Each session's due cards are one set. Between sets a corner note names the next one and
    // moves on after a short pause unless the learner stops; after the last set Today ends.
    function nudgesOn() {
        return readLocal(NUDGES_STORAGE_KEY, null)?.off !== true;
    }

    function setNudges(on) {
        writeLocal(NUDGES_STORAGE_KEY, { off: !on });
        if (on) {
            startReminderChecks();
            return true;
        }
        global.clearInterval(state.setBreakTimer);
        dismiss(d.querySelector('.memora-today-next-toast'));
        closeDoneCard();
        global.clearInterval(reminderInterval);
        reminderInterval = 0;
        return false;
    }

    function muteNudges() {
        setNudges(false);
        if (typeof showPopup === 'function') showPopup('Study nudges are off. Turn them back on anytime in Settings → Appearance.', null, false);
    }

    function muteButton() {
        const button = toastButton("Don't show again");
        button.classList.add('memora-nudge-mute');
        button.addEventListener('click', muteNudges);
        return button;
    }

    function finishSet(finishedId) {
        noteReviewStarted();
        if (!nudgesOn()) return;
        void refresh({ force: true }).then((summary) => {
            if (!nudgesOn()) return;
            const next = summary?.sessions.find((entry) => entry.id !== finishedId && entry.due > 0);
            if (next) showSetBreak(next);
            else if (summary?.hasHistory) showDoneCard(summary);
        });
    }

    function showSetBreak(next) {
        d.querySelector('.memora-today-next-toast')?.remove();
        closeDoneCard();
        global.clearInterval(state.setBreakTimer);
        const toast = d.createElement('div');
        toast.className = 'quiz-action-toast memora-today-next-toast';
        toast.setAttribute('role', 'status');
        const text = d.createElement('span');
        text.className = 'quiz-action-toast-message';
        text.textContent = `Set done. Next: ${next.name}, ${next.due} due.`;
        // The countdown is visual only, so a screen reader hears the note once.
        const count = d.createElement('span');
        count.className = 'memora-today-next-count';
        count.setAttribute('aria-hidden', 'true');
        const now = toastButton('Start now');
        const stop = toastButton('Stop');
        let left = SET_BREAK_SECONDS;
        const render = () => { count.textContent = `Starting in ${left}…`; };
        const finish = (go) => {
            global.clearInterval(state.setBreakTimer);
            dismiss(toast);
            if (go) void start(next.id);
        };
        now.addEventListener('click', () => finish(true));
        stop.addEventListener('click', () => finish(false));
        // A hidden tab holds the countdown, so a session never opens while nobody is looking.
        state.setBreakTimer = global.setInterval(() => {
            if (d.visibilityState === 'hidden') return;
            left -= 1;
            if (left <= 0) finish(true);
            else render();
        }, 1000);
        render();
        toast.append(text, count, now, stop, muteButton());
        d.body.appendChild(toast);
        global.requestAnimationFrame(() => toast.classList.add('show'));
    }

    function closeDoneCard() {
        const card = state.doneCard;
        state.doneCard = null;
        global.clearTimeout(state.doneTimer);
        dismiss(card);
    }

    function doneCardMarkup(summary) {
        const forecast = summary.forecast;
        const delta = forecast ? Math.round(forecast.delta * 100) : 0;
        const forecastLine = forecast
            ? `<p class="memora-today-done-forecast" title="${esc(forecastTitle(forecast))}">
                <span>${esc(forecast.examTitle ? `${forecast.label} · ${forecast.examTitle}` : forecast.label)}</span>
                <b>${esc(percent(forecast.value))}</b>${delta >= 1 ? `<em>+${delta} today</em>` : ''}
            </p>`
            : '';
        const weakest = forecast?.weakest?.length
            ? `<p class="memora-today-done-note">Weakest: ${forecast.weakest.map((source) => `${esc(source.label)} ${esc(percent(source.value))}`).join(', ')}</p>`
            : '';
        const tomorrowCopy = summary.tomorrow
            ? `Tomorrow: ${plural(summary.tomorrow, 'card')} · ~${summary.tomorrowMinutes} min`
            : 'Nothing comes due tomorrow.';
        const slot = readLocal(SLOT_STORAGE_KEY, null);
        const slotsAllowed = !(typeof IS_EMBED_DEMO !== 'undefined' && IS_EMBED_DEMO);
        const chips = slotChoices().map((minutes) => `<button type="button" class="memora-today-done-chip${slot?.minutes === minutes ? ' is-picked' : ''}" data-today-slot="${minutes}" aria-pressed="${slot?.minutes === minutes ? 'true' : 'false'}">${esc(formatMinutes(minutes))}</button>`).join('');
        const slotSection = slotsAllowed
            ? `<div class="memora-today-done-slot">
                <p class="memora-today-done-label">${slot ? 'Daily study time' : 'Pick a daily study time'}</p>
                <div class="memora-today-done-chips" role="group" aria-label="Daily study time">${chips}</div>
                <p class="memora-today-done-slot-result" aria-live="polite">${slot ? slotResultMarkup(slot) : ''}</p>
            </div>`
            : '';
        const reminder = reminderSettings();
        const reminderSection = 'Notification' in global
            ? `<label class="memora-today-done-remind">
                <input type="checkbox" data-today-reminder${reminder.on && !reminder.paused ? ' checked' : ''}>
                <span>Remind me once a day when cards are due</span>
            </label>
            ${reminder.on && reminder.paused ? '<p class="memora-today-done-note">Paused after three reminders went unopened. Tick it to turn it back on.</p>' : ''}`
            : '';
        return `<button type="button" class="memora-today-done-close" data-today-done="close" aria-label="Close">×</button>
            <p class="memora-live-dashboard-eyebrow">Done for today</p>
            <h2 class="memora-today-done-title">Today's review is clear</h2>
            ${forecastLine}
            ${weakest}
            <p class="memora-today-done-note">${esc(tomorrowCopy)}</p>
            ${slotSection}
            ${reminderSection}
            <button type="button" class="memora-today-done-mute" data-today-done="mute">Don't show this again</button>`;
    }

    // A corner card, never a dialog: it leaves after a while unless the pointer or focus is in it.
    function showDoneCard(summary) {
        if (!summary) return;
        closeDoneCard();
        d.querySelector('.memora-today-next-toast')?.remove();
        const card = d.createElement('section');
        card.className = 'memora-today-done';
        card.setAttribute('aria-label', 'Done for today');
        card.innerHTML = doneCardMarkup(summary);
        card.addEventListener('click', (event) => { void doneCardClick(event, card); });
        card.addEventListener('change', (event) => {
            const box = event.target?.closest?.('[data-today-reminder]');
            if (box) void setReminder(box.checked).then((on) => { box.checked = on; });
        });
        card.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') closeDoneCard();
        });
        d.body.appendChild(card);
        state.doneCard = card;
        const arm = () => {
            global.clearTimeout(state.doneTimer);
            state.doneTimer = global.setTimeout(() => {
                if (state.doneCard !== card) return;
                if (card.matches(':hover') || card.contains(d.activeElement)) arm();
                else closeDoneCard();
            }, DONE_CARD_MS);
        };
        arm();
        global.requestAnimationFrame(() => card.classList.add('show'));
    }

    async function doneCardClick(event, card) {
        if (event.target?.closest?.('[data-today-done="close"]')) {
            closeDoneCard();
            return;
        }
        if (event.target?.closest?.('[data-today-done="mute"]')) {
            muteNudges();
            return;
        }
        const ics = event.target?.closest?.('[data-today-slot-ics]');
        if (ics) {
            downloadSlotIcs();
            return;
        }
        const chip = event.target?.closest?.('[data-today-slot]');
        if (!chip || chip.disabled) return;
        const minutes = Number(chip.getAttribute('data-today-slot'));
        const chips = card.querySelectorAll('[data-today-slot]');
        const result = card.querySelector('.memora-today-done-slot-result');
        chips.forEach((button) => { button.disabled = true; });
        try {
            const slot = await saveStudySlot(minutes);
            chips.forEach((button) => {
                const picked = Number(button.getAttribute('data-today-slot')) === minutes;
                button.classList.toggle('is-picked', picked);
                button.setAttribute('aria-pressed', String(picked));
            });
            if (result) result.innerHTML = slotResultMarkup(slot);
        } catch (error) {
            console.warn('[Today review] The study time could not be saved.', error);
            if (result) result.textContent = 'Could not add it to your calendar. Try again.';
        } finally {
            chips.forEach((button) => { button.disabled = false; });
        }
    }

    /* --- Study slots ---------------------------------------------------------- */

    // The hours the learner actually starts Today's review, most recent twenty.
    function rememberStudyTime(now = Date.now()) {
        const hours = readLocal(HOURS_STORAGE_KEY, []);
        const list = (Array.isArray(hours) ? hours : []).filter(Number.isFinite);
        list.push(minutesOfDay(now));
        writeLocal(HOURS_STORAGE_KEY, list.slice(-20));
    }

    function habitMinutes() {
        const hours = readLocal(HOURS_STORAGE_KEY, []);
        const list = (Array.isArray(hours) ? hours : []).filter(Number.isFinite).sort((a, b) => a - b);
        if (list.length < 3) return DEFAULT_STUDY_MINUTES;
        return Math.round(list[Math.floor(list.length / 2)] / SLOT_MINUTES) * SLOT_MINUTES;
    }

    // The usual time and an hour and a half either side, inside the day.
    function slotChoices() {
        const clamp = (minutes) => Math.min(LATEST_SLOT_MINUTES, Math.max(EARLIEST_SLOT_MINUTES, minutes));
        const base = clamp(habitMinutes());
        return Array.from(new Set([base - 90, base, base + 90].map(clamp)));
    }

    function calendarApiReady() {
        return typeof prepareCalendarEvent === 'function' && typeof replaceCalendarEvents === 'function';
    }

    async function ensureCalendarApi() {
        if (calendarApiReady()) return true;
        try {
            await global.MemoraRuntime?.ensureFeature?.('calendar');
            await global.initCalendarModule?.();
        } catch (error) {
            console.warn('[Today review] Calendar could not load.', error);
        }
        return calendarApiReady();
    }

    function slotDraft(minutes, startDay, until) {
        return {
            title: 'Memoraful review',
            type: 'study',
            startDay,
            endDay: startDay,
            allDay: false,
            startMinutes: minutes,
            endMinutes: minutes + SLOT_MINUTES,
            recurrence: { freq: 'daily', interval: 1, until },
            notes: SLOT_NOTE
        };
    }

    // One daily study block from tomorrow for eight weeks, in the Memoraful calendar. A new
    // pick replaces the last one, so there is only ever one.
    async function saveStudySlot(minutes) {
        if (!(await ensureCalendarApi())) throw new Error('Calendar is not available.');
        const first = new Date();
        first.setHours(0, 0, 0, 0);
        first.setDate(first.getDate() + 1);
        const last = new Date(first);
        last.setDate(last.getDate() + SLOT_WEEKS * 7 - 1);
        const previous = readLocal(SLOT_STORAGE_KEY, null);
        const event = prepareCalendarEvent(slotDraft(minutes, localDayKey(first.getTime()), localDayKey(last.getTime())));
        await replaceCalendarEvents({ put: [event], remove: previous?.id ? [previous.id] : [] });
        const slot = { id: event.id, minutes, startDay: event.startDay, until: localDayKey(last.getTime()) };
        writeLocal(SLOT_STORAGE_KEY, slot);
        const reminder = reminderSettings();
        if (reminder.on) saveReminder({ minutes });
        return slot;
    }

    function googleCalendarLink(slot) {
        const stamp = (dayKey, minutes) => `${dayKey.replace(/-/g, '')}T${String(Math.floor(minutes / 60)).padStart(2, '0')}${String(minutes % 60).padStart(2, '0')}00`;
        const params = new URLSearchParams({
            action: 'TEMPLATE',
            text: 'Memoraful review',
            dates: `${stamp(slot.startDay, slot.minutes)}/${stamp(slot.startDay, slot.minutes + SLOT_MINUTES)}`,
            recur: `RRULE:FREQ=DAILY;UNTIL=${String(slot.until).replace(/-/g, '')}`,
            details: SLOT_NOTE
        });
        return `https://calendar.google.com/calendar/render?${params}`;
    }

    function slotResultMarkup(slot) {
        return `<span>Daily at ${esc(formatMinutes(slot.minutes))} in your Memoraful calendar.</span>
            <span>Also add it to <button type="button" class="memora-live-dashboard-text-action" data-today-slot-ics>your calendar app (.ics)</button>
            or <a class="memora-live-dashboard-text-action" href="${esc(googleCalendarLink(slot))}" target="_blank" rel="noopener noreferrer">Google Calendar</a>.</span>`;
    }

    // The .ics carries an alarm at the start, so the phone's own calendar does the reminding.
    function downloadSlotIcs() {
        const slot = readLocal(SLOT_STORAGE_KEY, null);
        if (!slot || typeof buildCalendarIcsDocument !== 'function' || typeof prepareCalendarEvent !== 'function') return;
        try {
            const event = prepareCalendarEvent({ ...slotDraft(slot.minutes, slot.startDay, slot.until), id: slot.id });
            const alarm = ['BEGIN:VALARM', 'TRIGGER:PT0M', 'ACTION:DISPLAY', 'DESCRIPTION:Memoraful review', 'END:VALARM'].join('\r\n');
            const ics = buildCalendarIcsDocument([event]).replace(/END:VEVENT/g, `${alarm}\r\nEND:VEVENT`);
            const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
            if (typeof triggerBlobDownload === 'function') {
                triggerBlobDownload(blob, 'memoraful-review.ics');
                return;
            }
            const url = URL.createObjectURL(blob);
            const anchor = d.createElement('a');
            anchor.href = url;
            anchor.download = 'memoraful-review.ics';
            anchor.click();
            global.setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (error) {
            console.warn('[Today review] The study time could not be exported.', error);
        }
    }

    /* --- Daily reminder ------------------------------------------------------- */

    // A browser notification from an open Memoraful tab; there is no server push, so with
    // every tab closed the calendar slot's own alarm is the reminder.
    function reminderSettings() {
        const stored = readLocal(REMINDER_STORAGE_KEY, null);
        return {
            on: false,
            minutes: 0,
            lastDay: '',
            opened: true,
            unopened: 0,
            paused: false,
            ...(stored && typeof stored === 'object' ? stored : {})
        };
    }

    function saveReminder(patch) {
        const next = { ...reminderSettings(), ...patch };
        writeLocal(REMINDER_STORAGE_KEY, next);
        return next;
    }

    // Asked for only from the learner's own tick, never on load.
    async function setReminder(on) {
        if (!on) {
            saveReminder({ on: false, paused: false });
            return false;
        }
        if (!('Notification' in global)) return false;
        let permission = global.Notification.permission;
        try {
            if (permission === 'default') permission = await global.Notification.requestPermission();
        } catch (_error) { /* treated as blocked */ }
        if (permission !== 'granted') {
            saveReminder({ on: false });
            if (typeof showPopup === 'function') showPopup('Notifications are blocked for this site. Allow them in your browser settings to get the daily reminder.', null, true);
            return false;
        }
        saveReminder({ on: true, paused: false, unopened: 0, opened: true, minutes: readLocal(SLOT_STORAGE_KEY, null)?.minutes || habitMinutes() });
        startReminderChecks();
        return true;
    }

    // Starting Today's review counts as answering the day's reminder, however it was opened.
    function noteReviewStarted() {
        const reminder = reminderSettings();
        if (reminder.lastDay === localDayKey() && !reminder.opened) saveReminder({ opened: true, unopened: 0 });
    }

    // At most one a day: at or after the study time, outside quiet hours, only when cards are
    // due and the learner is not already in the app. Three unopened in a row pause it.
    async function checkReminder(now = Date.now()) {
        const settings = reminderSettings();
        if (!settings.on || settings.paused || !nudgesOn() || !('Notification' in global) || global.Notification.permission !== 'granted') return;
        const minute = minutesOfDay(now);
        if (minute < settings.minutes || minute >= QUIET_FROM_MINUTES || minute < QUIET_UNTIL_MINUTES) return;
        const today = localDayKey(now);
        if (settings.lastDay === today) return;
        if (d.visibilityState === 'visible' && d.hasFocus?.()) return;
        const summary = await refresh({ force: true });
        if (!summary || summary.total <= 0) return;
        // Another tab may have sent today's while this one read; the day is claimed before showing.
        const fresh = reminderSettings();
        if (fresh.lastDay === today) return;
        const unopened = fresh.lastDay && !fresh.opened ? fresh.unopened + 1 : 0;
        if (unopened >= REMINDER_MAX_UNOPENED) {
            saveReminder({ paused: true, unopened, lastDay: today });
            return;
        }
        saveReminder({ lastDay: today, opened: false, unopened });
        try {
            const body = [`${plural(summary.total, 'card')} due · ~${summary.minutes} min`, summary.forecast ? `${forecastCopy(summary.forecast)} expected` : '']
                .filter(Boolean).join('. ');
            const note = new global.Notification("Today's review", { body, tag: 'memora-today' });
            note.onclick = () => {
                saveReminder({ opened: true, unopened: 0 });
                try { global.focus(); } catch (_error) { /* the browser decides */ }
                note.close();
                void start();
            };
        } catch (error) {
            console.warn('[Today review] The daily reminder could not be shown.', error);
        }
    }

    let reminderInterval = 0;
    function startReminderChecks() {
        if (reminderInterval || !reminderSettings().on || !nudgesOn()) return;
        reminderInterval = global.setInterval(() => { void checkReminder(); }, REMINDER_CHECK_MS);
    }

    /* --- Dashboard row -------------------------------------------------------- */

    function examLineMarkup(summary) {
        const exam = summary.exam;
        if (!exam) return '';
        const covered = exam.covered;
        const coverCopy = covered ? `covers ${covered} session${covered === 1 ? '' : 's'}` : 'no sessions linked';
        return `<div class="memora-live-dashboard-today-exam">
            <span><strong>${esc(exam.title)}</strong> · ${esc(formatDay(exam.time))} · ${esc(inDaysCopy(exam.days))} · ${esc(coverCopy)}</span>
            <button type="button" class="memora-live-dashboard-text-action" data-memora-dashboard-action="today-coverage" aria-expanded="false" aria-controls="memora-today-coverage">Change</button>
        </div>`;
    }

    // Rendered closed whenever there is an exam, like the inline calendar's surface: Change
    // only flips classes, so the card grows on a transition instead of a re-render.
    function coverageMarkup(summary) {
        const exam = summary.exam;
        if (!exam) return '';
        const names = state.sessionNames;
        const covered = new Set(coveredSessionIds(exam));
        const rows = Array.from(state.sessionActivity.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([id]) => {
                const name = names.get(id) || 'Study session';
                return `<label class="memora-live-dashboard-today-cover-row" title="${esc(name)}">
                <input type="checkbox" data-memora-today-cover="${esc(id)}"${covered.has(id) ? ' checked' : ''}>
                <span>${esc(name)}</span>
            </label>`;
            }).join('');
        // Always rendered closed; applyCoverage() opens it after each render, so the open state
        // never changes the markup and Polish never re-renders the header mid-motion.
        return `<div class="memora-live-dashboard-today-coverage" id="memora-today-coverage" role="group" aria-label="Sessions ${esc(exam.title)} covers" aria-hidden="true" inert>
            <div class="memora-live-dashboard-today-cover-inner">
                <div class="memora-live-dashboard-eyebrow">Sessions this exam covers</div>
                <h2 class="memora-live-dashboard-today-cover-title">${esc(exam.title)}</h2>
                <p class="memora-live-dashboard-today-cover-note">Cards from checked sessions are spaced to be seen before ${esc(exam.title)}, with a final pass on weak cards in the last two days.</p>
                <div class="memora-live-dashboard-today-cover-list">${rows || '<p class="memora-live-dashboard-empty">No saved sessions yet.</p>'}</div>
                <div class="memora-live-dashboard-today-cover-actions">
                    <button type="button" class="memora-live-dashboard-action" data-memora-dashboard-action="today-coverage-done">Done</button>
                </div>
            </div>
        </div>`;
    }

    // Empty until the learner has rated at least one card anywhere, so a new profile's
    // Dashboard does not gain another empty box.
    function dashboardMarkup() {
        const summary = state.summary;
        if (!summary || !summary.hasHistory) return '';
        const caughtUp = summary.total === 0;
        const chips = summary.sources.slice(0, MAX_SOURCE_CHIPS)
            .map((source) => `<span class="memora-live-dashboard-today-chip">${esc(source.label)} <b>${source.count}</b></span>`)
            .join('');
        const more = summary.sources.length > MAX_SOURCE_CHIPS
            ? `<span class="memora-live-dashboard-today-chip is-more">+${summary.sources.length - MAX_SOURCE_CHIPS} more</span>`
            : '';
        const sessionsCopy = summary.sessions.length > 1 ? ` · ${summary.sessions.length} sessions` : '';
        const nextCopy = summary.nextDueAt
            ? `Next review ${esc(inDaysCopy(daysUntil(summary.nextDueAt)))}.`
            : 'Nothing scheduled yet.';
        const title = caughtUp
            ? 'All caught up'
            : `${summary.total} due · ~${summary.minutes} min${sessionsCopy}`;
        const forecast = summary.forecast
            ? `<p class="memora-live-dashboard-today-note memora-today-forecast" title="${esc(forecastTitle(summary.forecast))}">${esc(forecastCopy(summary.forecast, !caughtUp))}</p>`
            : '';
        return `<section class="memora-live-dashboard-card memora-live-dashboard-today${caughtUp ? ' is-caught-up' : ''}" aria-label="Today's review">
            <div class="memora-live-dashboard-today-main">
                <div class="memora-live-dashboard-today-copy">
                    <div class="memora-live-dashboard-eyebrow">Today's review</div>
                    <h2 class="memora-live-dashboard-today-title">${esc(title)}</h2>
                    ${caughtUp
                        ? `<p class="memora-live-dashboard-today-note">${nextCopy}</p>`
                        : `<div class="memora-live-dashboard-today-chips">${chips}${more}</div>`}
                    ${forecast}
                </div>
                ${caughtUp ? '' : `<button type="button" class="memora-live-dashboard-action is-primary" data-memora-dashboard-action="today-start" title="Review the cards due today from each session's selected sources, mixed across lectures">Start</button>`}
            </div>
            ${examLineMarkup(summary)}
            ${coverageMarkup(summary)}
        </section>`;
    }

    /* The Study Desk is the Dashboard's one container: the exam sits in its header, today's
       review fills the left half, and Polish fills the right half with Continue studying. */
    function shortDaysCopy(days) {
        if (days <= 0) return 'today';
        if (days === 1) return 'tomorrow';
        return `${days} days`;
    }

    // asSubtitle: the line stands in for Coming up's subtitle under a headline that already
    // counts the days, so it leaves the countdown out.
    function deskExamMarkup(summary, asSubtitle = false) {
        const exam = summary?.exam;
        if (!exam) return '';
        const coverCopy = exam.covered ? `covers ${exam.covered} session${exam.covered === 1 ? '' : 's'}` : 'no sessions linked';
        const when = [formatDay(exam.time), asSubtitle ? '' : shortDaysCopy(exam.days), coverCopy].filter(Boolean).join(' · ');
        // The exam is the next one across every session, so the line says which sessions it covers.
        return `<div class="memora-live-dashboard-desk-exam${asSubtitle ? ' is-subtitle' : ''}">
            <strong title="${esc(exam.title)}">${esc(exam.title)}</strong>
            <span class="memora-live-dashboard-desk-when">${esc(when)}</span>
            <button type="button" class="memora-live-dashboard-text-action memora-live-dashboard-desk-link" data-memora-dashboard-action="today-coverage" aria-expanded="false" aria-controls="memora-today-coverage" title="${esc(exam.title)} ${esc(coverCopy)}. Change which sessions it covers.">Change</button>
        </div>`;
    }

    function deskReviewMarkup(summary) {
        const head = '<div class="memora-live-dashboard-eyebrow">Today\'s review</div>';
        if (!summary || !summary.hasHistory) {
            return `<div class="memora-live-dashboard-desk-cell memora-live-dashboard-desk-review is-empty">
                ${head}
                <p class="memora-live-dashboard-desk-figure is-quiet">Nothing yet</p>
                <p class="memora-live-dashboard-desk-note">Rated quiz cards come back here when they are due.</p>
            </div>`;
        }
        const forecast = summary.forecast
            ? `<span class="memora-today-forecast" title="${esc(forecastTitle(summary.forecast))}">${esc(forecastCopy(summary.forecast, summary.total > 0))}</span>`
            : '';
        if (summary.total === 0) {
            const nextCopy = summary.nextDueAt ? `Next review ${inDaysCopy(daysUntil(summary.nextDueAt))}.` : 'Nothing scheduled yet.';
            return `<div class="memora-live-dashboard-desk-cell memora-live-dashboard-desk-review is-caught-up">
                ${head}
                <p class="memora-live-dashboard-desk-figure is-quiet">All caught up</p>
                <p class="memora-live-dashboard-desk-note"><span>${esc(nextCopy)}</span>${forecast}</p>
            </div>`;
        }
        const count = summary.sessions.length;
        const rows = summary.sessions.map((entry) => `<button type="button" class="memora-live-dashboard-desk-breakdown-row" data-memora-dashboard-action="today-session" data-session-id="${esc(entry.id)}" title="Review ${esc(entry.name)}"><span>${esc(entry.name)}</span><b>${entry.due}</b></button>`).join('');
        return `<div class="memora-live-dashboard-desk-cell memora-live-dashboard-desk-review">
            ${head}
            <p class="memora-live-dashboard-desk-figure"><span class="memora-live-dashboard-desk-number">${summary.total}</span> due</p>
            <div class="memora-live-dashboard-desk-note">
                <span>~${summary.minutes} min</span>
                <span class="memora-live-dashboard-desk-sessions${state.breakdownOpen ? ' is-open' : ''}">
                    <button type="button" class="memora-live-dashboard-desk-sessions-btn" data-memora-dashboard-action="today-breakdown" aria-expanded="${state.breakdownOpen ? 'true' : 'false'}" aria-controls="memora-today-breakdown">${count} session${count === 1 ? '' : 's'}</button>
                    <span class="memora-live-dashboard-desk-breakdown" id="memora-today-breakdown" role="group" aria-label="Due by session">${rows}</span>
                </span>
                ${forecast}
            </div>
            <button type="button" class="memora-live-dashboard-action is-primary memora-live-dashboard-desk-go" data-memora-dashboard-action="today-start" title="Review the cards due today from each session's selected sources, mixed across lectures">Start review</button>
        </div>`;
    }

    function deskMarkup() {
        const summary = state.summary;
        return {
            exam: deskExamMarkup(summary),
            examSubtitle: deskExamMarkup(summary, true),
            examTitle: summary?.exam?.title || '',
            review: deskReviewMarkup(summary),
            coverage: summary ? coverageMarkup(summary) : ''
        };
    }

    /* Opens or closes the panel where it stands, the way the inline calendar's apply() does:
       the shell's hero row and the veil run on CSS transitions, so nothing re-renders mid-motion. */
    function applyCoverage() {
        const panel = d.getElementById('memora-today-coverage');
        const header = panel?.closest('.memora-live-dashboard-header');
        const shell = header?.closest('.memora-live-dashboard-shell');
        if (!panel || !header || !shell) return false;
        if (state.coverageOpen) {
            // A re-render while open must not drop the learner's unsaved ticks.
            if (state.coverageDraft) {
                panel.querySelectorAll('[data-memora-today-cover]').forEach((box) => {
                    box.checked = state.coverageDraft.has(box.getAttribute('data-memora-today-cover'));
                });
            }
            // Measured once per opening, so a re-render while open keeps the same height. The
            // card always grows visibly, like Calendar, even when the list would fit at rest.
            // Measuring a fresh header would flush its closed style and replay the grow.
            if (!state.coverageRow) {
                const inner = panel.querySelector('.memora-live-dashboard-today-cover-inner');
                const style = getComputedStyle(panel);
                const pad = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
                const rest = header.offsetHeight;
                state.coverageRow = Math.ceil(Math.max(rest + COVER_MIN_GROWTH, (inner?.offsetHeight || 0) + pad));
            }
            shell.style.setProperty('--memora-cover-row', `${state.coverageRow}px`);
        } else {
            state.coverageRow = 0;
        }
        header.classList.toggle(COVER_OPEN_CLASS, state.coverageOpen);
        shell.classList.toggle(COVER_OPEN_CLASS, state.coverageOpen);
        panel.inert = !state.coverageOpen;
        panel.setAttribute('aria-hidden', String(!state.coverageOpen));
        d.querySelectorAll('[data-memora-dashboard-action="today-coverage"]')
            .forEach((button) => button.setAttribute('aria-expanded', String(state.coverageOpen)));
        return true;
    }

    function waitForCoverClose() {
        if (global.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();
        return new Promise((resolve) => setTimeout(resolve, COVER_MOTION_MS));
    }

    function rerenderDashboard() {
        global.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail: state.summary }));
    }

    async function dashboardAction(action, button) {
        if (action === 'today-start') {
            void start();
            return;
        }
        if (action === 'today-session') {
            state.breakdownOpen = false;
            void start(button?.dataset?.sessionId || undefined);
            return;
        }
        if (action === 'today-breakdown') {
            state.breakdownOpen = !state.breakdownOpen;
            rerenderDashboard();
            return;
        }
        const exam = state.summary?.exam;
        if (action === 'today-coverage' && exam) {
            state.coverageOpen = !state.coverageOpen;
            state.coverageDraft = state.coverageOpen ? new Set(coveredSessionIds(exam)) : null;
            if (!applyCoverage()) rerenderDashboard();
            global.clearTimeout(state.coverageFocusTimer);
            // Focus once the card has mostly grown, as Calendar does; earlier focus scrolls mid-motion.
            if (state.coverageOpen) {
                state.coverageFocusTimer = global.setTimeout(() => {
                    if (state.coverageOpen) d.querySelector('[data-memora-today-cover]')?.focus({ preventScroll: true });
                }, 500);
            }
            return;
        }
        if (action === 'today-coverage-done') {
            const draft = state.coverageDraft;
            state.coverageOpen = false;
            state.coverageDraft = null;
            global.clearTimeout(state.coverageFocusTimer);
            applyCoverage();
            // The refresh re-renders the Dashboard, so it waits for the card to finish folding.
            await Promise.all([
                exam && draft ? saveCoverage(exam.id, Array.from(draft)) : null,
                waitForCoverClose()
            ]);
            await refresh({ force: true });
            d.querySelector('[data-memora-dashboard-action="today-coverage"]')?.focus({ preventScroll: true });
        }
    }

    // Ticking a box only edits the draft, so the row does not re-render under the pointer.
    d.addEventListener('change', (event) => {
        const box = event.target?.closest?.('[data-memora-today-cover]');
        if (!box || !state.coverageDraft) return;
        const id = box.getAttribute('data-memora-today-cover');
        if (box.checked) state.coverageDraft.add(id);
        else state.coverageDraft.delete(id);
    });

    /* --- Wiring --------------------------------------------------------------- */

    global.addEventListener('memora:spaced-review-complete', (event) => {
        if (event.detail?.seenOnly) finishSet(String(event.detail.sessionId || ''));
        else void refresh({ force: true });
    });
    // A rating changes what is due; the next Dashboard visit re-reads. The event fires before
    // the scheduler moves the card, so the forecast log sees the prediction it is testing.
    global.addEventListener('memora:stats-updated', (event) => {
        logCalibration(event);
        state.readAt = 0;
    });
    d.addEventListener('visibilitychange', () => {
        if (d.visibilityState === 'visible') state.readAt = 0;
    });

    global.MemoraReviewSchedule = Object.freeze({
        version: 2,
        refresh,
        summary: () => state.summary,
        start,
        clampNextReview,
        isExamCrunchDue,
        forecastCheck,
        showDoneForToday: () => showDoneCard(state.summary),
        nudgesOn,
        setNudges,
        dashboardMarkup,
        deskMarkup,
        // Polish calls this after each Dashboard render, as it mounts the inline calendar.
        mountDashboard: () => { applyCoverage(); },
        dashboardAction
    });

    const boot = () => {
        // After first paint; the Dashboard asks again when it opens. Today's count lives on the
        // Dashboard only: a Quizzes rail badge read as a count of quizzes.
        global.setTimeout(() => { void refresh({ force: true }); }, 1500);
        startReminderChecks();
    };
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot, { once: true });
    else boot();
}(window));
